import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { AdvisorDeliveryInput } from "@/store/AppStore";
import NumberInput from "@/components/base/NumberInput";
import type { InventoryItem, Product } from "@/types";
import { costAdvisor, type AdvisorAccount } from "@/utils/accounting";
import { formatCOP } from "@/utils/format";

type Mode = "consignacion" | "compra" | "devolucion";

interface DeliveryModalProps {
  open: boolean;
  account: AdvisorAccount | null;
  products: Product[];
  inventory: InventoryItem[];
  onClose: () => void;
  onSubmit: (input: AdvisorDeliveryInput) => void;
}

const DeliveryModal = ({ open, account, products, inventory, onClose, onSubmit }: DeliveryModalProps) => {
  const [mode, setMode] = useState<Mode>("consignacion");
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(1);
  const [from, setFrom] = useState<"casa" | "clear">("casa");
  const [unitValue, setUnitValue] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setMode("consignacion");
    setProductId("");
    setQty(1);
    setFrom("casa");
    setError("");
  }, [open, products]);

  // Solo se ofrecen productos con stock real: en consignación/venta los que hay
  // en Oficina, y en devolución los que ese asesor tiene en consignación.
  const availableProducts = useMemo(() => {
    return products.filter((p) => {
      const inv = inventory.find((i) => i.productId === p.id);
      if (!inv) return false;
      if (mode === "devolucion") {
        const consigned = account ? inv.stockByAdvisor[account.advisor.id] ?? 0 : 0;
        return consigned > 0;
      }
      const available = from === "casa" ? inv.stockCasa : inv.stockPuntoClear;
      return available > 0;
    });
  }, [products, inventory, mode, from, account]);

  // Si el producto elegido se queda sin stock (o cambió el modo), se salta al
  // primer producto disponible para no dejar el formulario apuntando a uno vacío.
  useEffect(() => {
    if (!open) return;
    if (!availableProducts.some((p) => p.id === productId)) {
      setProductId(availableProducts[0]?.id ?? "");
    }
  }, [open, availableProducts, productId]);

  const product = useMemo(() => products.find((p) => p.id === productId) ?? null, [products, productId]);
  const margin = account?.margin ?? 25;
  const suggested = product ? costAdvisor(product.pricePublic, margin) : 0;

  useEffect(() => {
    setUnitValue(suggested);
  }, [suggested]);

  const inv = inventory.find((i) => i.productId === productId);
  const fromStock = inv ? (from === "casa" ? inv.stockCasa : inv.stockPuntoClear) : 0;
  const consignedNow = account && inv ? inv.stockByAdvisor[account.advisor.id] ?? 0 : 0;

  const submit = () => {
    if (!account) return;
    if (!product) {
      setError("Selecciona un producto.");
      return;
    }
    const q = Number(qty) || 0;
    if (q <= 0) {
      setError("La cantidad debe ser mayor a cero.");
      return;
    }
    if (mode === "devolucion") {
      if (q > consignedNow) {
        setError(`El asesor solo tiene ${consignedNow} unidades en consignación.`);
        return;
      }
    } else if (q > fromStock) {
      setError(`Solo hay ${fromStock} unidades disponibles en esa ubicación.`);
      return;
    }
    if (mode === "consignacion" && account.advisor.consignmentLimit > 0) {
      const extra = q * (Number(unitValue) || suggested);
      if (account.consignedValue + extra > account.advisor.consignmentLimit) {
        setError(`Supera el cupo de consignación (${formatCOP(account.advisor.consignmentLimit)}).`);
        return;
      }
    }
    onSubmit({
      advisorId: account.advisor.id,
      advisorName: `${account.advisor.names} ${account.advisor.surnames}`.trim(),
      productId: product.id,
      productName: product.name,
      qty: q,
      mode,
      from: mode === "devolucion" ? "casa" : from,
      unitValue: Number(unitValue) || 0,
    });
  };

  const MODES: { key: Mode; label: string }[] = [
    { key: "consignacion", label: "Dejar en consignación" },
    { key: "compra", label: "Venderle al asesor" },
    { key: "devolucion", label: "Devolución al admin" },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Mercancía · ${account ? `${account.advisor.names} ${account.advisor.surnames}` : ""}`}
      icon="ri-hand-heart-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-save-3-line" /> Registrar movimiento
          </button>
        </>
      }
    >
      <div className="mb-4">
        <label className={labelClass}>Tipo de movimiento</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              className={`rounded-md border px-3 py-2 text-[11px] font-semibold cursor-pointer transition-colors ${
                mode === m.key
                  ? "border-accent-500/50 bg-accent-500/10 text-accent-200"
                  : "border-background-300/50 text-foreground-600 hover:border-accent-500/30"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelClass}>Producto</label>
          <select value={productId} onChange={(e) => setProductId(e.target.value)} className={inputClass}>
            {availableProducts.length === 0 && (
              <option value="">
                {mode === "devolucion" ? "Sin mercancía en consignación" : "Sin productos con stock en Oficina"}
              </option>
            )}
            {availableProducts.map((p) => (
              <option key={p.id} value={p.id} className="bg-background-100">
                {p.sku} · {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Cantidad (unidades)</label>
          <NumberInput min={1} value={qty} onChange={setQty} className={inputClass} />
        </div>

        {mode !== "devolucion" ? (
          <div>
            <label className={labelClass}>Sale de</label>
            <select value={from} onChange={(e) => setFrom(e.target.value as "casa" | "clear")} className={inputClass}>
              <option value="casa" className="bg-background-100">
                Oficina (admin)
              </option>
            </select>
          </div>
        ) : (
          <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
            <p className="text-[10px] text-foreground-500">En consignación hoy</p>
            <p className="text-sm font-bold text-foreground-950">{consignedNow} und</p>
          </div>
        )}

        <div>
          <label className={labelClass}>Valor unitario (costo asesor)</label>
          <NumberInput min={0} value={unitValue} onChange={setUnitValue} className={inputClass} />
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2 flex flex-col justify-center">
          <p className="text-[10px] text-foreground-500">
            {mode === "compra" ? "Total a cobrar al asesor" : "Valor de referencia"}
          </p>
          <p className="text-sm font-bold text-accent-200">
            {formatCOP((Number(unitValue) || 0) * (Number(qty) || 0))}
          </p>
        </div>
      </div>

      <p className="text-[10px] text-foreground-500 mt-3">
        {mode === "consignacion" && "La mercancía pasa a consignación: el asesor la paga cuando la venda."}
        {mode === "compra" && "Venta al asesor: la mercancía pasa a ser suya y queda como cuenta por cobrar."}
        {mode === "devolucion" && "El asesor devuelve mercancía y regresa al inventario del admin (Casa)."}
      </p>

      {error && (
        <p className="text-[11px] text-primary-200 mt-2 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default DeliveryModal;